<script setup>
import {usePortalContentAndDesignStore} from "../../../../../../stores/partners/portalContentAndDesignStore";
import EditableResourceImageField
    from "../../../../portal-editable-fields/resources/EditableResourceImageField.vue";
import EditableTextField from "../../../../portal-editable-fields/EditableTextField.vue";
import EditableResourceTextEditorField
    from "../../../../portal-editable-fields/resources/EditableResourceTextEditorField.vue";
import {ref, computed} from "vue";

const stepIcons = {
    'user-plus': '<path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/>',
    'share': '<circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>',
    'dollar': '<line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/>',
    'gift': '<polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/><line x1="12" y1="22" x2="12" y2="7"/><path d="M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z"/>',
    'star': '<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>',
    'check-circle': '<path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>',
    'megaphone': '<path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>',
    'trending-up': '<polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/>',
    'mail': '<path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>',
    'rocket': '<path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 00-2.91-.09z"/><path d="M12 15l-3-3a22 22 0 012-3.95A12.88 12.88 0 0122 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 01-4 2z"/><path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/><path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/>',
    'heart': '<path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>',
    'link': '<path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/>',
};

const props = defineProps({
    resource: {
        type: Object,
        required: true,
    },
});

const portalContentAndDesignStore = usePortalContentAndDesignStore();

const useIcons = computed(() => props.resource.display_mode === 'icons');

const getIconSvg = (iconId) => stepIcons[iconId] || stepIcons['star'];

const hoverMainContainer = ref(false);

const alignmentClass = computed(() => {
    switch (props.resource.alignment) {
        case 'center': return 'justify-center text-center';
        case 'right': return 'justify-end text-right';
        default: return 'justify-start text-left';
    }
});

const headerLayoutClass = computed(() => {
    const position = props.resource.main_image_position || 'left';
    const alignment = props.resource.alignment || 'left';

    if (position === 'top') {
        if (alignment === 'center') return 'flex flex-col items-center gap-4';
        if (alignment === 'right') return 'flex flex-col items-end gap-4';
        return 'flex flex-col items-start gap-4';
    }

    if (alignment === 'center') return 'flex flex-col items-center gap-4';
    return 'flex items-center gap-6';
});

const imageSizeClass = computed(() => {
    if (props.resource.main_image_size === undefined && props.resource.main_image_retina_resize !== undefined) {
        return props.resource.main_image_retina_resize ? 'max-w-8' : 'max-w-16';
    }
    const size = props.resource.main_image_size || 'medium';
    switch (size) {
        case 'small':
        case '@2x': return 'max-w-8';
        case 'large': return 'max-w-24';
        default: return 'max-w-16';
    }
});

const stepsCount = computed(() => props.resource.contents?.length || 0);

const gridColsClass = computed(() => {
    if (stepsCount.value <= 1) return '';
    if (stepsCount.value === 2) return 'md:grid-cols-2';
    if (stepsCount.value === 3) return 'md:grid-cols-3';
    if (stepsCount.value === 4) return 'md:grid-cols-4';
    return 'md:grid-cols-5';
});

const accentLineClass = computed(() => {
    return portalContentAndDesignStore.getPageContentTextColorClass
        .replace('text-', 'bg-') + ' bg-opacity-15';
});
</script>

<template>
    <div>
        <div :class="[headerLayoutClass, alignmentClass]"
             @mouseenter="hoverMainContainer = true"
             @mouseleave="hoverMainContainer = false"
             :id="resource.unique_id"
        >
            <editable-resource-image-field
                resource-image-path="main_image"
                :resource-content="resource"
                :img-class="imageSizeClass"
                img-name="main_image"
                :show-add-image-button="hoverMainContainer"
            />

            <div class="space-y-1.5">
                <editable-text-field
                    v-if="resource.title"
                    text-type="h3"
                    :class="['text-xl md:text-2xl font-semibold', portalContentAndDesignStore.getPageContentTextColorClass]"
                    v-model="resource.title"
                />
                <editable-resource-text-editor-field
                    v-if="resource.description"
                    :class="[
                        'partnero-page-content-section text-base md:text-lg text-opacity-80 space-y-1',
                        portalContentAndDesignStore.getPageContentTextColorClass
                    ]"
                    :resource="resource"
                    resource-field="description"
                />
            </div>
        </div>

        <template v-if="resource.contents?.length">
            <div :class="[(resource.title || resource.description) ? 'mt-6 md:mt-8' : '']">

                <div :class="[
                    stepsCount > 2
                        ? 'flex gap-6 md:gap-8 overflow-x-auto pb-2 -mb-2'
                        : 'grid gap-6 md:gap-8 ' + gridColsClass
                ]">
                    <div
                        v-for="(step, index) in resource.contents"
                        :key="'step-' + step.unique_id"
                        :class="[
                            'space-y-3',
                            stepsCount > 2 ? 'min-w-[200px] flex-1' : ''
                        ]"
                    >
                        <div class="flex items-center gap-3">
                            <svg
                                v-if="useIcons && step.icon"
                                class="w-6 h-6 md:w-7 md:h-7 flex-shrink-0"
                                :class="portalContentAndDesignStore.getPageContentTextColorClass"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                viewBox="0 0 24 24"
                                v-html="getIconSvg(step.icon)"
                            ></svg>
                            <span
                                v-else
                                :class="['text-2xl md:text-3xl font-bold', portalContentAndDesignStore.getPageContentTextColorClass]"
                            >
                                {{ index + 1 }}
                            </span>
                            <div :class="['flex-1 h-px', accentLineClass]"></div>
                        </div>
                        <div class="space-y-1.5">
                            <editable-text-field
                                v-if="step.title"
                                text-type="custom"
                                :class="['text-base md:text-lg font-semibold', portalContentAndDesignStore.getPageContentTextColorClass]"
                                v-model="step.title"
                            />
                            <editable-text-field
                                v-if="step.description"
                                text-type="custom"
                                :class="['text-sm md:text-base text-opacity-60', portalContentAndDesignStore.getPageContentTextColorClass]"
                                v-model="step.description"
                            />
                        </div>
                    </div>
                </div>

            </div>
        </template>
    </div>
</template>
